import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  InputAdornment,
  FormHelperText,
  Chip,
  Typography,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  AccountBalance as FinanceIcon,
  SupportAgent as SupportIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  LocationCity as CityIcon,
  ContactPhone,
  NotificationsActive,
} from '@mui/icons-material';
import { User, IFuncionario } from '../../types';
import { CreateUserRequest, UpdateUserRequest } from '../../services/userService';
import userService from '../../services/userService';
import funcionarioService from '../../services/funcionarioService';
import grupoUsuarioService, { GrupoUsuario, PermissionCatalogResponse } from '../../services/grupoUsuarioService';
import EscritorioSelect from '../selects/EscritorioSelect';
import { formatarPhoneOnInput } from '../../utils/formatToView';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
  loading?: boolean;
  onSuccess?: () => void;
}

interface FormData {
  nome: string;
  nome_resumido?: string;
  usuario: string;
  email: string;
  senha?: string;
  confirmarSenha?: string;
  perfil: 'administrador' | 'financeiro' | 'suporte' | 'bot' | string;
  telefone?: string;
  funcionario_id?: number | null;
  escritorio_ids: number[];
  grupo_id?: number | null;
  permissoes_custom?: Record<string, 'grant' | 'deny'>;
  sigame?: boolean;
  ticket_novo?: boolean;
  telefones_sigame?: string;
  sigame_hora_inicio?: string;
  sigame_hora_fim?: string;
}

interface FormErrors {
  nome?: string;
  nome_resumido?: string;
  usuario?: string;
  email?: string;
  senha?: string;
  confirmarSenha?: string;
  perfil?: string;
  telefone?: string;
  funcionario_id?: string;
  escritorio_ids?: string;
  grupo_id?: string;
  permissoes_custom?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-permissoes-tabpanel-${index}`}
      aria-labelledby={`user-permissoes-tab-${index}`}
      {...other}
      style={{ flex: 1, overflowY: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `user-permissoes-tab-${index}`,
    'aria-controls': `user-permissoes-tabpanel-${index}`,
  };
}

const initialFormData: FormData = {
  nome: '',
  nome_resumido: '',
  usuario: '',
  email: '',
  senha: '',
  confirmarSenha: '',
  perfil: 'suporte',
  telefone: '',
  funcionario_id: null,
  escritorio_ids: [],
  grupo_id: null,
  permissoes_custom: {},
  sigame: false,
  ticket_novo: false,
  telefones_sigame: '',
  sigame_hora_inicio: '07:00',
  sigame_hora_fim: '22:00',
};

const perfilOptions = [
  {
    value: 'administrador',
    label: 'Administrador',
    icon: <AdminIcon />,
    color: 'primary' as const,
    description: 'Acesso total ao sistema'
  },
  {
    value: 'financeiro',
    label: 'Financeiro',
    icon: <FinanceIcon />,
    color: 'secondary' as const,
    description: 'Gestão financeira e relatórios'
  },
  {
    value: 'suporte',
    label: 'Suporte',
    icon: <SupportIcon />,
    color: 'warning' as const,
    description: 'Atendimento e suporte técnico'
  },
  {
    value: 'bot',
    label: 'Bot',
    icon: <BotIcon />,
    color: 'default' as const,
    description: 'Usuário automatizado do sistema'
  },
];

const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  user,
  loading = false,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [funcionarios, setFuncionarios] = useState<IFuncionario[]>([]);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [grupos, setGrupos] = useState<GrupoUsuario[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogResponse | null>(null);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const isEditing = !!user;
  const title = isEditing ? 'Editar Usuário' : 'Novo Usuário';

  // Carregar funcionários ativos
  useEffect(() => {
    const loadFuncionarios = async () => {
      try {
        setLoadingFuncionarios(true);
        const response = await funcionarioService.getFuncionariosAtivos();
        if (response.success) {
          setFuncionarios(response.data || []);
        }
      } catch (error) {
        console.error('Erro ao carregar funcionários:', error);
      } finally {
        setLoadingFuncionarios(false);
      }
    };

    if (open) {
      loadFuncionarios();
    }
  }, [open]);

  // Carregar grupos e catálogo de permissões
  useEffect(() => {
    const loadGruposAndCatalog = async () => {
      try {
        setLoadingGrupos(true);
        const [gruposData, catalogData] = await Promise.all([
          grupoUsuarioService.list(),
          grupoUsuarioService.getCatalog(),
        ]);
        setGrupos(gruposData || []);
        setCatalog(catalogData || { permissions: {}, modules: {} });
      } catch (error) {
        console.error('Erro ao carregar grupos:', error);
        setGrupos([]);
        setCatalog({ permissions: {}, modules: {} });
      } finally {
        setLoadingGrupos(false);
      }
    };

    if (open) {
      loadGruposAndCatalog();
    }
  }, [open]);

  // Carregar dados do usuário para edição
  useEffect(() => {
    if (isEditing && user) {
      const initialPermissoes: Record<string, 'grant' | 'deny'> = {};
      let permissoesCustomObj = user.permissoes_custom as any;

      if (typeof permissoesCustomObj === 'string') {
        try {
          permissoesCustomObj = JSON.parse(permissoesCustomObj);
        } catch (e) {
          permissoesCustomObj = null;
        }
      }

      if (permissoesCustomObj) {
        if (Array.isArray(permissoesCustomObj.grants)) {
          permissoesCustomObj.grants.forEach((code: string) => initialPermissoes[code] = 'grant');
        }
        if (Array.isArray(permissoesCustomObj.denies)) {
          permissoesCustomObj.denies.forEach((code: string) => initialPermissoes[code] = 'deny');
        }
      }

      setFormData({
        nome: user.nome || '',
        nome_resumido: user.nome_resumido || '',
        usuario: user.usuario || '',
        email: user.email || '',
        senha: '',
        confirmarSenha: '',
        perfil: user.perfil || 'suporte',
        telefone: user.telefone || '',
        funcionario_id: user.funcionario_id || null,
        escritorio_ids: Array.isArray(user.escritorio_ids) ? user.escritorio_ids :
          (typeof user.escritorio_ids === 'string' ?
            (() => {
              try {
                return JSON.parse(user.escritorio_ids) || [];
              } catch {
                return [];
              }
            })() : []),
        grupo_id: user.grupo_id || null,
        permissoes_custom: initialPermissoes,
        sigame: user.sigame ?? false,
        ticket_novo: user.ticket_novo ?? false,
        telefones_sigame: user.telefones_sigame || '',
        sigame_hora_inicio: user.sigame_hora_inicio || '07:00',
        sigame_hora_fim: user.sigame_hora_fim || '22:00',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setSubmitError('');
  }, [user, isEditing, open]);

  // Limpar formulário ao fechar
  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
      setErrors({});
      setSubmitError('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo ao digitar
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Validação em tempo real para alguns campos
    if (field === 'confirmarSenha' && formData.senha !== value) {
      setErrors(prev => ({ ...prev, confirmarSenha: 'As senhas não coincidem' }));
    } else if (field === 'senha' && formData.confirmarSenha && formData.confirmarSenha !== value) {
      setErrors(prev => ({ ...prev, confirmarSenha: 'As senhas não coincidem' }));
    }
  };

  const handleSelectChange = (field: keyof FormData) => (
    event: any
  ) => {
    const value = event.target.value;

    // Tratar especificamente funcionario_id e grupo_id como números
    if (field === 'funcionario_id' || field === 'grupo_id') {
      const processedValue = value === '' ? null : Number(value);
      setFormData(prev => ({ ...prev, [field]: processedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEscritoriosChange = (escritorios: string | number | number[] | string[] | null) => {
    // Garantir que sempre seja um array de números
    let numericEscritorios: number[] = [];

    if (Array.isArray(escritorios)) {
      numericEscritorios = escritorios.filter(id => typeof id === 'number') as number[];
    } else if (typeof escritorios === 'number') {
      numericEscritorios = [escritorios];
    }

    setFormData(prev => ({ ...prev, escritorio_ids: numericEscritorios }));
    if (errors.escritorio_ids) {
      setErrors(prev => ({ ...prev, escritorio_ids: undefined }));
    }
  };

  const handlePermissionToggle = (code: string, type: 'grant' | 'deny') => {
    setFormData(prev => {
      const newPermissoes = { ...prev.permissoes_custom };
      if (newPermissoes[code] === type) {
        // Se já tem esse tipo, remove
        delete newPermissoes[code];
      } else {
        // Se não tem ou é o outro tipo, define o novo
        newPermissoes[code] = type;
      }
      return { ...prev, permissoes_custom: newPermissoes };
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validação do nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formData.nome_resumido && formData.nome_resumido.trim().length > 50) {
      newErrors.nome_resumido = 'Nome resumido deve ter no máximo 50 caracteres';
    }

    // Validação do usuário
    if (!formData.usuario.trim()) {
      newErrors.usuario = 'Nome de usuário é obrigatório';
    } else if (formData.usuario.trim().length < 3) {
      newErrors.usuario = 'Nome de usuário deve ter pelo menos 3 caracteres';
    } else if (formData.usuario.trim().length > 50) {
      newErrors.usuario = 'Nome de usuário deve ter no máximo 50 caracteres';
    } else if (!/^[a-zA-Z0-9_.-]+$/.test(formData.usuario.trim())) {
      newErrors.usuario = 'Nome de usuário deve conter apenas letras, números, underscore, ponto e hífen';
    }

    // Validação do email
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Email deve ter um formato válido';
    }

    // Validação da senha (obrigatória apenas no cadastro)
    if (!isEditing) {
      if (!formData.senha) {
        newErrors.senha = 'Senha é obrigatória';
      } else if (formData.senha.length < 6) {
        newErrors.senha = 'Senha deve ter pelo menos 6 caracteres';
      }

      if (!formData.confirmarSenha) {
        newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
      } else if (formData.senha !== formData.confirmarSenha) {
        newErrors.confirmarSenha = 'As senhas não coincidem';
      }
    } else if (formData.senha && formData.senha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'As senhas não coincidem';
    }

    // Validação do telefone (opcional)
    if (formData.telefone && !/^[\d\s\-\(\)\+]+$/.test(formData.telefone)) {
      newErrors.telefone = 'Telefone deve conter apenas números e caracteres válidos';
    } else if (formData.telefone && formData.telefone.replace(/\D/g, '').length !== 11) {
      newErrors.telefone = 'Telefone deve ter 11 dígitos';
    }

    // Validação do perfil
    if (!formData.perfil) {
      newErrors.perfil = 'Perfil é obrigatório';
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const customPermsToSave: { grants: string[], denies: string[] } = { grants: [], denies: [] };
      Object.entries(formData.permissoes_custom || {}).forEach(([code, type]) => {
        if (type === 'grant') customPermsToSave.grants.push(code);
        else if (type === 'deny') customPermsToSave.denies.push(code);
      });
      const hasCustomPerms = customPermsToSave.grants.length > 0 || customPermsToSave.denies.length > 0;

      const userData: CreateUserRequest | UpdateUserRequest = {
        nome: formData.nome.trim(),
        nome_resumido: formData.nome_resumido?.trim() || null,
        usuario: formData.usuario.trim(),
        email: formData.email.trim(),
        perfil: formData.perfil as any,
        telefone: formData.telefone?.trim() || undefined,
        funcionario_id: formData.funcionario_id || null,
        escritorio_ids: formData.escritorio_ids.length > 0 ? formData.escritorio_ids : undefined,
        grupo_id: formData.grupo_id || null,
        permissoes_custom: hasCustomPerms ? customPermsToSave : undefined,
        sigame: formData.sigame,
        ticket_novo: formData.ticket_novo,
        telefones_sigame: formData.telefones_sigame?.trim() || null,
        sigame_hora_inicio: formData.sigame ? (formData.sigame_hora_inicio?.trim() || null) : null,
        sigame_hora_fim: formData.sigame ? (formData.sigame_hora_fim?.trim() || null) : null,
      };

      // Incluir senha apenas se foi preenchida
      if (!isEditing) {
        // Modo criação - senha é obrigatória
        (userData as CreateUserRequest).senha = formData.senha || '';
      } else if (formData.senha && formData.senha.trim() !== '') {
        // Modo edição - senha é opcional, mas se fornecida, deve ser incluída
        (userData as UpdateUserRequest).senha = formData.senha;
      }

      // Executar a lógica de criação/edição
      if (isEditing && user) {
        // Modo edição
        await userService.updateUser(user.id, userData as UpdateUserRequest);
      } else {
        // Modo criação
        await userService.createUser(userData as CreateUserRequest);
      }

      // Fechar modal e notificar sucesso
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setSubmitError(error.message || 'Erro ao salvar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPerfilIcon = (perfil: string) => {
    const option = perfilOptions.find(opt => opt.value === perfil);
    return option?.icon || <SupportIcon />;
  };

  const getPerfilColor = (perfil: string) => {
    const option = perfilOptions.find(opt => opt.value === perfil);
    return option?.color || 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getPerfilIcon(formData.perfil)}
          {title}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Nome */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome Completo"
                value={formData.nome}
                onChange={handleInputChange('nome')}
                error={!!errors.nome}
                helperText={errors.nome}
                required
                disabled={isSubmitting}
                size="small"
              />
            </Grid>

            {/* Nome Resumido */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome Resumido"
                value={formData.nome_resumido}
                onChange={handleInputChange('nome_resumido')}
                error={!!errors.nome_resumido}
                helperText={errors.nome_resumido || 'Nome para exibição em atendimento, e outras interfaces'}
                disabled={isSubmitting}
                size="small"
              />
            </Grid>

            {/* Usuário */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome de Usuário"
                value={formData.usuario}
                onChange={handleInputChange('usuario')}
                error={!!errors.usuario}
                helperText={errors.usuario || 'Usado para login no sistema'}
                required
                disabled={isSubmitting}
                size="small"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
                disabled={isSubmitting}
                size="small"
              />
            </Grid>

            {/* Telefone */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.telefone}
                onChange={(e) => {
                  const formatted = formatarPhoneOnInput(e.target.value, 11);
                  setFormData(prev => ({ ...prev, telefone: formatted }));
                  handleInputChange('telefone');
                }}
                error={!!errors.telefone}
                helperText={errors.telefone || 'Formato: (11) 99999-9999'}
                disabled={isSubmitting}
                placeholder="(11) 99999-9999"
                size="small"
                InputProps={{
                  startAdornment: <ContactPhone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>

            {/* Senha */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isEditing ? "Nova Senha" : "Senha"}
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={handleInputChange('senha')}
                error={!!errors.senha}
                helperText={errors.senha || (isEditing ? 'Deixe em branco para manter a atual' : 'Mínimo 6 caracteres com letras e números')}
                required={!isEditing}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            {/* Confirmar Senha */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirmar Senha"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmarSenha}
                onChange={handleInputChange('confirmarSenha')}
                error={!!errors.confirmarSenha}
                helperText={errors.confirmarSenha}
                required={!isEditing || !!formData.senha}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            {/* Perfil */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.perfil}>
                <InputLabel>Perfil</InputLabel>
                <Select
                  value={formData.perfil}
                  onChange={handleSelectChange('perfil')}
                  label="Perfil"
                  disabled={isSubmitting}
                  size="small"
                >
                  {perfilOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon}
                        <Box>
                          <div>{option.label}</div>
                          <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {option.description}
                          </div>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.perfil && <FormHelperText>{errors.perfil}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Funcionário Vinculado */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.funcionario_id}>
                <InputLabel>Funcionário Vinculado</InputLabel>
                <Select
                  value={formData.funcionario_id || ''}
                  onChange={handleSelectChange('funcionario_id')}
                  label="Funcionário Vinculado"
                  disabled={isSubmitting || loadingFuncionarios}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Nenhum funcionário vinculado</em>
                  </MenuItem>
                  {funcionarios.map((funcionario) => (
                    <MenuItem key={funcionario.id} value={funcionario.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        <Box>
                          <div>{funcionario.nome}</div>
                          <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {funcionario.cargo} - {funcionario.cpf}
                          </div>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.funcionario_id && <FormHelperText>{errors.funcionario_id}</FormHelperText>}
                <FormHelperText>
                  Opcional. Vincula o usuário a um funcionário específico.
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Escritórios */}
            <Grid item xs={12} sm={6}>
              <EscritorioSelect
                value={formData.escritorio_ids}
                onChange={handleEscritoriosChange}
                label="Escritórios de Atuação"
                placeholder="Selecione os escritórios"
                error={!!errors.escritorio_ids}
                helperText={errors.escritorio_ids || "Escritórios onde o usuário pode atuar"}
                disabled={isSubmitting}
                size="small"
                multiple={true}
                onlyActive={true}
                allowClear={true}
              />
            </Grid>

            {/* Grupo de Usuários */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.grupo_id}>
                <InputLabel>Grupo de Usuários</InputLabel>
                <Select
                  value={formData.grupo_id || ''}
                  onChange={handleSelectChange('grupo_id')}
                  label="Grupo de Usuários"
                  disabled={isSubmitting || loadingGrupos}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Sem grupo definido</em>
                  </MenuItem>
                  {(grupos || []).map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <div>{grupo.nome}</div>
                          {grupo.descricao && (
                            <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {grupo.descricao}
                            </div>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.grupo_id && <FormHelperText>{errors.grupo_id}</FormHelperText>}
                <FormHelperText>
                  Grupo que define as permissões base do usuário
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Siga-me */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <NotificationsActive color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Siga-me (Atendimentos)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.sigame}
                    onChange={(e) => setFormData(prev => ({ ...prev, sigame: e.target.checked }))}
                    disabled={isSubmitting}
                  />
                }
                label="Ativar Siga-me"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.ticket_novo}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_novo: e.target.checked }))}
                    disabled={isSubmitting || !formData.sigame}
                  />
                }
                label="Notificar novo ticket"
              />
            </Grid>

            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Telefones Siga-me"
                value={formData.telefones_sigame}
                onChange={handleInputChange('telefones_sigame')}
                disabled={isSubmitting || !formData.sigame}
                size="small"
                placeholder="5511999999999,5531888888888"
                helperText="DDI + DDD + número, separados por vírgula"
              />
            </Grid>

            <Grid item xs={12} sm={2.5}>
              <TextField
                fullWidth
                label="Horário inicial"
                type="time"
                value={formData.sigame_hora_inicio}
                onChange={handleInputChange('sigame_hora_inicio')}
                disabled={isSubmitting || !formData.sigame}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>

            <Grid item xs={12} sm={2.5}>
              <TextField
                fullWidth
                label="Horário final"
                type="time"
                value={formData.sigame_hora_fim}
                onChange={handleInputChange('sigame_hora_fim')}
                disabled={isSubmitting || !formData.sigame}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              //helperText="Notificações só são enviadas dentro deste intervalo"
              />
            </Grid>

            {/* Permissões Customizadas */}
            {catalog && catalog.modules && Object.keys(catalog.modules).length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 0, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Permissões Customizadas (Overrides)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Clique em um chip para alternar entre permitir, negar ou remover o override
                    </Typography>
                  </Box>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      aria-label="módulos de permissões customizadas"
                    >
                      {Object.keys(catalog.modules).map((modulo, index) => (
                        <Tab key={modulo} label={modulo} {...a11yProps(index)} sx={{ textTransform: 'capitalize' }} />
                      ))}
                    </Tabs>
                  </Box>
                  <Box sx={{ minHeight: 250, maxHeight: 400, overflowY: 'auto' }}>
                    {Object.entries(catalog.modules).map(([modulo, permissions], index) => {
                      // Group permissions by their sub-module
                      const groupedPermissions: Record<string, typeof permissions> = { 'Geral': [] };
                      permissions.forEach(p => {
                        const parts = p.code.split('.');
                        if (parts.length >= 3 && parts[0] === modulo) {
                          const groupName = parts[1].replace(/_/g, ' ');
                          if (!groupedPermissions[groupName]) {
                            groupedPermissions[groupName] = [];
                          }
                          groupedPermissions[groupName].push(p);
                        } else {
                          groupedPermissions['Geral'].push(p);
                        }
                      });

                      // Remove 'Geral' if empty
                      if (groupedPermissions['Geral'].length === 0) {
                        delete groupedPermissions['Geral'];
                      }

                      return (
                        <CustomTabPanel key={modulo} value={activeTab} index={index}>
                          {Object.entries(groupedPermissions).map(([groupName, groupPerms]) => (
                            <Box key={groupName} sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                                {groupName}
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                                {groupPerms.map((perm) => (
                                  <Box key={perm.code} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                                    <Typography variant="caption" sx={{ lineHeight: 1.2, fontWeight: 500, fontSize: '0.8rem' }}>{perm.label}</Typography>
                                    <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider', mx: 1, mt: 1 }} />
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Chip
                                        label="Permitir"
                                        size="small"
                                        variant={formData.permissoes_custom?.[perm.code] === 'grant' ? 'filled' : 'outlined'}
                                        onClick={() => handlePermissionToggle(perm.code, 'grant')}
                                        color={formData.permissoes_custom?.[perm.code] === 'grant' ? 'success' : 'default'}
                                        sx={{
                                          flex: 1,
                                          fontSize: '0.7rem',
                                          height: 24,
                                          fontWeight: formData.permissoes_custom?.[perm.code] === 'grant' ? 'bold' : 'normal',
                                          cursor: 'pointer',
                                          '&:hover': { opacity: 0.8 }
                                        }}
                                      />
                                      <Chip
                                        label="Negar"
                                        size="small"
                                        variant={formData.permissoes_custom?.[perm.code] === 'deny' ? 'filled' : 'outlined'}
                                        onClick={() => handlePermissionToggle(perm.code, 'deny')}
                                        color={formData.permissoes_custom?.[perm.code] === 'deny' ? 'error' : 'default'}
                                        sx={{
                                          flex: 1,
                                          fontSize: '0.7rem',
                                          height: 24,
                                          fontWeight: formData.permissoes_custom?.[perm.code] === 'deny' ? 'bold' : 'normal',
                                          cursor: 'pointer',
                                          '&:hover': { opacity: 0.8 }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </CustomTabPanel>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Visualização do perfil selecionado */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    icon={getPerfilIcon(formData.perfil)}
                    label={perfilOptions.find(opt => opt.value === formData.perfil)?.label}
                    color={getPerfilColor(formData.perfil)}
                    variant="outlined"
                  />
                </Box>
                <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                  {perfilOptions.find(opt => opt.value === formData.perfil)?.description}
                </div>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || loading}
            sx={{ minWidth: 120 }}
          >
            {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Usuário'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm; 
